import * as React from 'react';
import { PaginatedDashboardContext } from '../shared/paginated-dashboard-context';
import useRestService from '../shared/rest-service';
import Dashboard from '../models/dashboard';
import FetchOptions from '../components/management/pagination/fetch-options';
import useTimedAlerts from './useTimedAlerts';

interface FetchOptionsForClient extends FetchOptions {
  clientId: number;
}

/**
 * @function usePaginatedDashboards - Custom hook for the dashboard management page, with pagination context
 */
const usePaginatedDashboards = () => {
  const { getAuth } = useRestService();

  const { loading, dispatch, results, totalResults } = React.useContext(PaginatedDashboardContext);
  const { errorMessage, setErrorMessage } = useTimedAlerts();

  /**
   * fetchDashboardsPage
   * @function fetchDashboards - Fetches the dashboards from the backend and sets them to the state
   * @param options - FetchOptions
   */
  const fetchDashboards = ({
    currentPage,
    pageSize,
    searchTerm,
    clientId,
    orderColumn,
    orderDirection
  }: FetchOptionsForClient) => {
    dispatch({ loading: true });
    let queryParams = `?page=${currentPage}&pageSize=${pageSize}`;
    if (searchTerm && searchTerm.trim()) {
      queryParams += `&searchTerm=${searchTerm}`;
    }
    if (orderColumn != null) {
      queryParams += `&sort=${orderColumn}`;
    }
    if (orderDirection != null) {
      queryParams += `&sortDirection=${orderDirection}`;
    }
    return getAuth(`api/${clientId}/dashboards${queryParams}`)
      .then((response: { results: Dashboard[]; totalItems: number }) => {
        dispatch({
          loading: false,
          currentPage: currentPage,
          pageSize: pageSize,
          results: response.results,
          totalResults: response.totalItems,
          orderColumn,
          orderDirection,
          searchTerm
        });
      })
      .catch(() => setErrorMessage('Failed to get dashboards.'));
  };

  return {
    loading,
    results,
    fetchDashboards,
    totalResults,
    errorMessage
  };
};

export default usePaginatedDashboards;
